
import { useCallback, useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { type PowerInfo } from '@/lib/powers-data';
import { analytics } from '@/services/analytics';
import { getEverypowerPawnTooltipData, type EverypowerPawnTooltipData } from '@/lib/everypower-data';
import { EverypowerPawnTooltipBubble } from '@/components/help-center/powers/EverypowerPawnTooltip';
import {
  GsImage,
  ORBITAL_ICON_BOX_CLASS,
  copyText,
  getPowerCodeFontSize,
  cn,
} from '@/components/help-center/latest-power-shared';
import {
  GALLERY_BOX_STYLE,
  PowerGalleryViewToggle,
  PowerSectionSmileyIcon,
  PawnBadgeIcon,
  SmileySvgBadgeIcon,
  galleryBadgeClass,
  galleryCodeTextClass,
  usePowerGalleryViewMode,
  type PowerGalleryViewMode,
} from '@/components/help-center/powers/power-gallery-view';
import {
  GalleryHoverOverlay,
  galleryItemHoverClass,
  galleryMediaHoverClass,
} from '@/components/help-center/powers/gallery-hover';
import { EverypowerPawnsFootnotes } from '@/components/help-center/powers/PowerEverypowerSection';
import { isCollectionPower } from '@/lib/collections-data';

interface PowerSmiliesPawnsSectionProps {
  power: PowerInfo;
  productKind?: 'power' | 'ace';
}

function CardLabel({
  text,
  viewMode,
  isCopied,
  fontSize,
}: {
  text: string;
  viewMode: PowerGalleryViewMode;
  isCopied?: boolean;
  fontSize?: number;
}) {
  return (
    <div className={galleryBadgeClass(viewMode, isCopied)}>
      <span
        className={cn(
          'whitespace-nowrap font-poppins font-semibold leading-none',
          galleryCodeTextClass(viewMode)
        )}
        style={fontSize ? { fontSize: `${fontSize}px` } : undefined}
      >
        {text}
      </span>
    </div>
  );
}

function SmilieCard({
  code,
  itemKey,
  copiedKey,
  onCopy,
  viewMode,
}: {
  code: string;
  itemKey: string;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
  viewMode: PowerGalleryViewMode;
}) {
  const chat = `(${code})`;
  const isCopied = copiedKey === itemKey;

  return (
    <button
      type="button"
      onClick={() => onCopy(itemKey, chat)}
      title={`Copiar ${chat}`}
      className={cn(
        ORBITAL_ICON_BOX_CLASS,
        'group relative flex w-full min-w-0 flex-col items-center gap-2 rounded-xl p-3',
        galleryItemHoverClass(viewMode)
      )}
      style={GALLERY_BOX_STYLE[viewMode]}
    >
      <GalleryHoverOverlay mode="copy" isCopied={isCopied} />
      <GsImage
        code={code}
        className={cn('flex h-14 w-14 items-center justify-center', galleryMediaHoverClass)}
        imgClassName="max-h-[52px] max-w-[52px] object-contain"
      />
      <CardLabel
        text={viewMode === 'light' ? chat : code}
        viewMode={viewMode}
        isCopied={isCopied}
        fontSize={getPowerCodeFontSize(viewMode === 'light' ? chat : code)}
      />
    </button>
  );
}

function PawnCard({
  pawn,
  pawnKey,
  copiedKey,
  onCopy,
  viewMode,
  tooltip,
}: {
  pawn: { hat: string; name: string };
  pawnKey: string;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
  viewMode: PowerGalleryViewMode;
  tooltip?: EverypowerPawnTooltipData | null;
}) {
  const isCopied = copiedKey === pawnKey;

  return (
    <div className="relative overflow-visible">
      {tooltip ? (
        <span
          className="group/info pointer-events-auto absolute right-1.5 top-1.5 z-40"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <span
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-full shadow-md ring-1',
              viewMode === 'dark'
                ? 'bg-[#0a1450] text-sky-300 ring-blue-900/60'
                : 'bg-white text-gray-500 ring-black/10'
            )}
            aria-label={tooltip.text}
          >
            <Info className="h-3 w-3" aria-hidden />
          </span>
          <EverypowerPawnTooltipBubble data={tooltip} viewMode={viewMode} />
        </span>
      ) : null}
      <button
        type="button"
        onClick={() => onCopy(pawnKey, pawn.hat)}
        title={tooltip ? undefined : `Copiar ${pawn.hat}`}
        className={cn(
          ORBITAL_ICON_BOX_CLASS,
          'group relative flex w-full min-w-0 flex-col items-center gap-2 rounded-xl p-3',
          galleryItemHoverClass(viewMode)
        )}
        style={GALLERY_BOX_STYLE[viewMode]}
      >
        <GalleryHoverOverlay mode="copy" isCopied={isCopied} />
        <GsImage
          code={pawn.name}
          className={cn('flex h-14 w-14 items-center justify-center', galleryMediaHoverClass)}
          imgClassName="max-h-[52px] max-w-[52px] object-contain"
        />
        <CardLabel
          text={pawn.hat}
          viewMode={viewMode}
          isCopied={isCopied}
          fontSize={getPowerCodeFontSize(pawn.hat)}
        />
      </button>
    </div>
  );
}

export default function PowerSmiliesPawnsSection({
  power,
  productKind = 'power',
}: PowerSmiliesPawnsSectionProps) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const { mode, setMode } = usePowerGalleryViewMode();
  const productLabel = productKind === 'ace' ? 'Ace' : 'power';

  const isCollection = isCollectionPower(power);
  const showSmilies = power.smilies.length > 0;
  const showPawns = !isCollection && power.pawns.length > 0;
  const sectionTitle = isCollection ? 'Smilies da Coleção' : 'Smilies e Peões';

  const handleCopy = useCallback(async (key: string, text: string) => {
    const ok = await copyText(text);
    if (ok) {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1800);
    }
  }, []);

  useEffect(() => {
    if (!showSmilies) return;
    void analytics.powerViewSmilies({
      powerId: power.id,
      slug: power.slug,
      action: 'view_smilies',
    });
  }, [showSmilies, power.id, power.slug]);

  if (!showSmilies && !showPawns) return null;

  return (
    <section className="border-b border-gray-100 bg-white py-14">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <PowerSectionSmileyIcon code="biggrin" size="md" />
            <h2 className="font-poppins text-xl font-bold text-gray-900 md:text-2xl">{sectionTitle}</h2>
          </div>
          <PowerGalleryViewToggle mode={mode} onChange={setMode} className="shrink-0" />
        </div>

        <div className="mb-10 flex flex-wrap gap-3">
          {showSmilies ? (
            <div className="inline-flex items-center gap-1.5 rounded-xl border border-amber-100 bg-amber-50/60 px-3.5 py-2.5">
              <SmileySvgBadgeIcon className="text-amber-700" />
              <span className="font-poppins text-sm font-semibold text-amber-800">
                {power.smilies.length} {power.smilies.length === 1 ? 'smilie' : 'smilies'}
              </span>
            </div>
          ) : null}
          {showPawns ? (
            <div className="inline-flex items-center gap-1 rounded-xl border border-emerald-100 bg-emerald-50/60 px-3.5 py-2.5">
              <PawnBadgeIcon className="text-emerald-700" />
              <span className="font-poppins text-sm font-semibold leading-none text-emerald-800">
                {power.pawns.length} {power.pawns.length === 1 ? 'peão' : 'peões'}
              </span>
            </div>
          ) : null}
        </div>

        {showSmilies ? (
          <div className={showPawns ? 'mb-12' : undefined}>
            <div className="mb-5">
              <h3 className="font-poppins text-base font-bold text-gray-900">Smilies</h3>
              <p className="mt-1 font-inter text-sm text-gray-500">
                {isCollection
                  ? `Smilies exclusivos da coleção ${power.name}, desbloqueados ao completá-la. Clique para copiar o código no chat.`
                  : `Smilies incluídos no ${productLabel} ${power.name}. Clique para copiar o código no chat.`}
              </p>
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(108px,1fr))] gap-2.5 overflow-visible">
              {power.smilies.map((code, index) => (
                <SmilieCard
                  key={`sm-${index}-${code}`}
                  code={code}
                  itemKey={`sm-${index}-${code}`}
                  copiedKey={copiedKey}
                  onCopy={handleCopy}
                  viewMode={mode}
                />
              ))}
            </div>
          </div>
        ) : null}

        {showPawns ? (
          <div>
            <div className="mb-5">
              <h3 className="font-poppins text-base font-bold text-gray-900">Peões</h3>
              <p className="mt-1 font-inter text-sm text-gray-500">
                {power.slug === 'everypower'
                  ? 'Oito variantes do peão Esmeralda. Com o power Hat activo, clique num peão para copiar o código e aplicá-lo ao seu nome.'
                  : `Peões disponíveis no ${productLabel} ${power.name}. Clique para copiar o código do peão.`}
              </p>
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(108px,1fr))] gap-2.5 overflow-visible">
              {power.pawns.map((pawn, i) => (
                <PawnCard
                  key={`${i}-${pawn.hat}`}
                  pawn={pawn}
                  pawnKey={`pawn-${i}-${pawn.hat}`}
                  copiedKey={copiedKey}
                  onCopy={handleCopy}
                  viewMode={mode}
                  tooltip={
                    power.slug === 'everypower'
                      ? getEverypowerPawnTooltipData(pawn.hat)
                      : null
                  }
                />
              ))}
            </div>
            {power.slug === 'everypower' ? <EverypowerPawnsFootnotes /> : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
